import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { useConfig } from '../config.js';
import { getDisplayUnits, galToL, lToGal } from '../units.js';
import { formatSalinity, readSalinityUnit, salinityUnitLabel, salinityToPpt } from '../salinity.js';
import { saltForChange, matchBrand, saltSupplyYield } from '../salt-mix.js';
import { isFreshwaterTank } from '../first-run.js';

// Log a water change in one sheet: pick the amount, pick the salt, get
// the mixing guide for that brand at this tank's salinity target, save.
// The POST consumes the linked salt supply (in the supply's own unit)
// and ticks the open water-change task, so this one tap closes the loop.

const PRESETS = [10, 15, 20];

export function WaterChangeSheet({ tank, onClose, onDone }) {
  const config = useConfig();
  const units = getDisplayUnits(tank);
  // A freshwater tank mixes no salt: no salt box, no mixing guide, and no
  // salt supply made on save (feedback #74 review).
  const fresh = isFreshwaterTank(tank);
  const isLiters = units.volume === 'L';

  // Working volume is what actually holds water; fall back to the label
  // volume when Tank build hasn't been filled in.
  const volumeGal = Number(tank.volume_water_gal) || Number(tank.volume_gallons) || 0;
  // Detection-first: legacy targets stored on the S.G. scale (1.026)
  // convert instead of poisoning the mixing math.
  const targetPpt = salinityToPpt(tank.params?.salinity?.target) || 35;
  const salinityUnit = readSalinityUnit();

  const saltSupplies = useMemo(
    () => (tank.supplies || []).filter((s) => (s.category || '').toLowerCase() === 'salt'),
    [tank.supplies]
  );
  const brands = config?.salt_mixes?.brands || [];
  const disclaimer = config?.salt_mixes?.disclaimer || '';

  const [percent, setPercent] = useState(10);
  const [customAmount, setCustomAmount] = useState(''); // in display units
  const [supplyId, setSupplyId] = useState(saltSupplies[0]?.id || '');
  const [brandKey, setBrandKey] = useState(() => {
    const m = matchBrand(brands, saltSupplies[0]?.name);
    return m?.key || brands[0]?.key || '';
  });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recent, setRecent] = useState(null);
  // First water change with no salt tracked: set the box up right
  // here instead of sending the keeper to the Supplies tab (the old
  // dead end). Opt-in ('none' default): auto-creating the default
  // brand's first package fabricated inventory the keeper never
  // bought (review finding). Index into brand.packages, or 'none'.
  const [newPkgIdx, setNewPkgIdx] = useState('none');
  // Survives a failed water-change POST so the retry reuses the
  // already-created box instead of creating a duplicate.
  const createdSaltIdRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api(`/api/tanks/${tank.id}/water-changes?limit=3`)
      .then((rows) => alive && setRecent(rows))
      .catch(() => alive && setRecent([]));
    return () => {
      alive = false;
    };
  }, [tank.id]);

  // Gallons being changed — from the preset % of working volume, or the
  // custom amount typed in the keeper's display unit.
  const gallons = useMemo(() => {
    if (percent === 'custom') {
      const n = Number(String(customAmount).replace(',', '.'));
      if (!Number.isFinite(n) || n <= 0) return 0;
      return isLiters ? lToGal(n) : n;
    }
    return volumeGal > 0 ? (volumeGal * percent) / 100 : 0;
  }, [percent, customAmount, volumeGal, isLiters]);

  const brand = fresh ? null : brands.find((b) => b.key === brandKey) || null;
  const guide =
    brand && gallons > 0
      ? saltForChange({
          gallons,
          gPerL: brand.g_per_l,
          targetPpt,
          cupsPerGal: brand.cups_per_gal,
        })
      : null;

  const displayAmount = (gal) =>
    isLiters ? `${Number(galToL(gal).toFixed(1))} L` : `${Number(gal.toFixed(1))} gal`;

  const pickSupply = (id) => {
    setSupplyId(id);
    const s = saltSupplies.find((x) => x.id === id);
    const m = matchBrand(brands, s?.name);
    if (m) setBrandKey(m.key);
  };

  const save = async () => {
    if (!(gallons > 0)) {
      setErr(
        volumeGal > 0
          ? 'Pick an amount first.'
          : 'Set a tank volume (Tank settings) or enter a custom amount.'
      );
      return;
    }
    setBusy(true);
    setErr('');
    try {
      let saltSupplyId = fresh ? null : supplyId || null;
      if (!saltSupplyId && saltSupplies.length === 0 && newPkgIdx !== 'none' && brand) {
        if (createdSaltIdRef.current) {
          saltSupplyId = createdSaltIdRef.current;
        } else {
          const pkg = brand.packages?.[Number(newPkgIdx)];
          const box = pkg ? saltSupplyYield(pkg.gal, units.volume) : null;
          if (pkg && box) {
            const created = await api(`/api/tanks/${tank.id}/supplies`, {
              method: 'POST',
              body: {
                name: `${brand.label} — ${pkg.label}`,
                category: 'salt',
                unit: box.unit,
                size: box.size,
                remaining: box.size,
                daily_use: 0,
              },
            });
            createdSaltIdRef.current = created.id;
            saltSupplyId = created.id;
          }
        }
      }
      await api(`/api/tanks/${tank.id}/water-changes`, {
        method: 'POST',
        body: {
          gallons: Number(gallons.toFixed(2)),
          percent: percent === 'custom' ? null : percent,
          salt_supply_id: saltSupplyId,
          salt_brand: brand?.label || null,
          salt_grams: guide?.grams || null,
          note: note.trim() || null,
        },
      });
    } catch (e) {
      setErr(e.message || 'Could not log the water change.');
      setBusy(false);
      return;
    }
    // Logged. A refresh that fails on a weak signal must not reopen the
    // form: a second tap logged a second water change and drained the salt
    // box twice.
    try {
      await onDone?.();
    } catch {
      /* the tank reloads on its next visit */
    }
    onClose();
  };

  const box = {
    padding: 12,
    borderRadius: 12,
    background: 'var(--surface)',
    border: '1px solid var(--surface-border)',
  };
  const label = {
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--text-dim)',
    marginBottom: 6,
  };
  const chip = (active) => ({
    minHeight: 44,
    padding: '8px 14px',
    borderRadius: 999,
    fontSize: 14,
    letterSpacing: '0.08em',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
    background: active ? 'var(--accent-bg)' : 'transparent',
    color: active ? 'var(--cyan)' : 'var(--text-muted)',
    fontWeight: active ? 600 : 400,
  });
  // Dropdowns read as plain text without an affordance — a friend
  // couldn't tell "Don't track my salt" was a *choice*. Give the selects
  // a real field look (border + chevron) so it's obvious they're tappable.
  const fieldSelect = {
    width: '100%',
    background: 'var(--surface-2, rgba(127,127,127,.10))',
    border: '1px solid var(--surface-border)',
    borderRadius: 10,
    outline: 'none',
    padding: '11px 34px 11px 12px',
    fontSize: 16,
    color: 'var(--text)',
    appearance: 'none',
    cursor: 'pointer',
  };
  const caret = {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: 'var(--text-muted)',
    fontSize: 14,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="slide-in"
        style={{
          width: '100%',
          maxWidth: 448,
          maxHeight: '92vh',
          overflowY: 'auto',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: '18px 22px max(28px, calc(env(safe-area-inset-bottom) + 16px))',
          background: 'var(--sheet-bg)',
          borderTop: '1px solid var(--surface-border)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            margin: '0 auto 16px',
            background: 'var(--surface-border)',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <h3
            className="font-heading"
            style={{ fontSize: 15, letterSpacing: '0.15em', textTransform: 'uppercase', margin: 0 }}
          >
            Log a water change
          </h3>
          <button
            onClick={onClose}
            aria-label="Close water change sheet"
            className="hdr-gear"
            style={{ flexShrink: 0 }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={box}>
            <div className="font-heading" style={label}>
              How much
              {volumeGal > 0 ? ` · of ${displayAmount(volumeGal)} working water` : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="font-heading"
                  style={chip(percent === p)}
                  onClick={() => setPercent(p)}
                  disabled={volumeGal <= 0}
                >
                  {p}%
                </button>
              ))}
              <button
                type="button"
                className="font-heading"
                style={chip(percent === 'custom')}
                onClick={() => setPercent('custom')}
              >
                Custom
              </button>
              {percent === 'custom' && (
                <input
                  type="number"
                  inputMode="decimal"
                  autoFocus
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={isLiters ? 'L' : 'gal'}
                  style={{
                    width: 90,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid var(--surface-border)',
                    outline: 'none',
                    padding: '6px 4px',
                    fontSize: 17,
                    color: 'var(--text)',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  }}
                />
              )}
            </div>
            {gallons > 0 && (
              <div style={{ marginTop: 8, fontSize: 15, color: 'var(--text-muted)' }}>
                {fresh ? 'New water:' : 'New water to mix:'}{' '}
                <strong style={{ color: 'var(--text)' }}>{displayAmount(gallons)}</strong>
              </div>
            )}
            {fresh && (
              <div
                style={{ marginTop: 6, fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5 }}
              >
                Treat tap water with a dechlorinator and bring it to the tank&rsquo;s temperature
                before it goes in.
              </div>
            )}
          </div>

          {!fresh && (
            <div style={box}>
              <div className="font-heading" style={label}>
                Salt
              </div>
              {saltSupplies.length > 0 ? (
                <div style={{ position: 'relative' }}>
                  <select
                    value={supplyId}
                    onChange={(e) => pickSupply(e.target.value)}
                    style={fieldSelect}
                  >
                    {saltSupplies.map((s) => (
                      <option
                        key={s.id}
                        value={s.id}
                        style={{ background: 'var(--sheet-bg)', color: 'var(--text)' }}
                      >
                        {s.name}
                        {s.remaining != null
                          ? ` — ${s.remaining}${s.unit ? ` ${s.unit}` : ''} left`
                          : ''}
                      </option>
                    ))}
                    <option
                      value=""
                      style={{ background: 'var(--sheet-bg)', color: 'var(--text)' }}
                    >
                      Don&rsquo;t track against a supply
                    </option>
                  </select>
                  <span style={caret} aria-hidden="true">
                    &#9662;
                  </span>
                </div>
              ) : (
                <>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={newPkgIdx}
                      onChange={(e) => setNewPkgIdx(e.target.value)}
                      style={fieldSelect}
                    >
                      {(brand?.packages || []).map((pkg, i) => (
                        <option
                          key={pkg.label}
                          value={i}
                          style={{ background: 'var(--sheet-bg)', color: 'var(--text)' }}
                        >
                          New box: {brand.label} — {pkg.label}
                        </option>
                      ))}
                      <option
                        value="none"
                        style={{ background: 'var(--sheet-bg)', color: 'var(--text)' }}
                      >
                        Don&rsquo;t track my salt
                      </option>
                    </select>
                    <span style={caret} aria-hidden="true">
                      &#9662;
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-dim)',
                      lineHeight: 1.5,
                      marginTop: 4,
                    }}
                  >
                    {newPkgIdx === 'none'
                      ? 'Not tracking salt. Pick your box above to start — every water change then draws it down and Sensei warns before it runs out.'
                      : 'Tracked from this change on — every water change draws the box down and Sensei warns before it runs out.'}
                  </div>
                </>
              )}
              <div className="font-heading" style={{ ...label, marginTop: 10 }}>
                Brand (for the mixing guide)
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  value={brandKey}
                  onChange={(e) => setBrandKey(e.target.value)}
                  style={fieldSelect}
                >
                  {brands.map((b) => (
                    <option
                      key={b.key}
                      value={b.key}
                      style={{ background: 'var(--sheet-bg)', color: 'var(--text)' }}
                    >
                      {b.label}
                    </option>
                  ))}
                </select>
                <span style={caret} aria-hidden="true">
                  &#9662;
                </span>
              </div>
            </div>
          )}

          {guide && (
            <div
              style={{
                ...box,
                background: 'var(--accent-bg)',
                border: '1px solid var(--accent-border)',
              }}
            >
              <div className="font-heading" style={{ ...label, color: 'var(--cyan)' }}>
                Mixing guide · {brand.label}
              </div>
              <div style={{ fontSize: 16, color: 'var(--text)', lineHeight: 1.6 }}>
                For {displayAmount(gallons)} at {formatSalinity(targetPpt, salinityUnit)}{' '}
                {salinityUnitLabel(salinityUnit)}:{' '}
                <strong>
                  ~{guide.grams} g{guide.cups != null ? ` (≈ ${guide.cups} cups)` : ''}
                </strong>{' '}
                of dry mix.
              </div>
              <ol
                style={{
                  margin: '8px 0 0',
                  paddingLeft: 18,
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  lineHeight: 1.7,
                }}
              >
                <li>Add the salt to RO/DI water, never the other way around.</li>
                <li>Mix with a pump or powerhead until fully clear.</li>
                <li>Heat to tank temperature before it goes in.</li>
                <li>Verify salinity with your refractometer, then change away.</li>
              </ol>
              {disclaimer && (
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-dim)' }}>
                  {disclaimer}
                </div>
              )}
            </div>
          )}

          <div style={box}>
            <div className="font-heading" style={label}>
              Note (optional)
            </div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Vacuumed the sand bed too"
              style={{
                width: '100%',
                minHeight: 44,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '4px 0',
                fontSize: 16,
                color: 'var(--text)',
              }}
            />
          </div>

          {err && (
            <div role="alert" style={{ fontSize: 15, color: 'var(--alert)' }}>
              {err}
            </div>
          )}

          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="font-heading"
            style={{
              minHeight: 44,
              padding: '12px 0',
              borderRadius: 12,
              background: 'var(--cyan)',
              color: 'var(--cyan-text)',
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? 'Logging…' : 'Log water change'}
          </button>

          {recent && recent.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <div className="font-heading" style={label}>
                Recent changes
              </div>
              {recent.map((w) => (
                <div
                  key={w.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    padding: '4px 0',
                  }}
                >
                  <span>
                    {displayAmount(Number(w.gallons))}
                    {w.percent ? ` (${Math.round(w.percent)}%)` : ''}
                    {w.salt_brand ? ` · ${w.salt_brand}` : ''}
                  </span>
                  <span className="font-mono" style={{ color: 'var(--text-dim)', flexShrink: 0 }}>
                    {new Date(w.ts).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
